/**
 * ============================================================================
 * MCP Knowledge Adapter — PLACEHOLDER (Commercial Operator V0.01)
 * ============================================================================
 *
 * Cumple `KnowledgePort` (ver /src/domain/ports/KnowledgePort.js).
 *
 * ROL EN LA PLATAFORMA
 * --------------------
 * Este adaptador es la única puerta por la cual el conocimiento organizacional
 * del cliente entra al ciclo de decisión de Commercial Operator. No modela
 * el conocimiento, no lo escribe: lo lee y lo entrega como snippets al motor
 * que lo pide, para que el LLM adapter lo inyecte en la generación de plays.
 *
 * Cuando esté implementado, este adaptador conectará contra un servidor MCP
 * (Model Context Protocol) que expone cuatro subsistemas de conocimiento
 * lógicamente distintos pero accedidos por la misma superficie del port:
 *
 *   1. PRODUCT BIBLE
 *      Descripción del producto/servicio del cliente: qué vende, para quién,
 *      pricing tiers, features, comparación vs competencia, límites técnicos.
 *      El LLM lo usa para hablar del producto sin inventar features.
 *
 *   2. KNOWLEDGE BASE
 *      Cuerpo operativo del cliente: casos de éxito, objection handling,
 *      argumentos ganadores, restricciones legales, tono de comunicación,
 *      ICP empírico. El LLM lo usa para hablar como la empresa habla.
 *
 *   3. DECISION LOG
 *      Historial de outcomes anteriores enriquecido con contexto: qué plays
 *      cerraron qué segmentos, qué mensajes convirtieron y en qué ventana.
 *      El motor (Intelligence + LLM) lo usa para no repetir intentos fallidos
 *      y para escalar patrones ganadores.
 *
 *   4. AI OPERATING MANUAL
 *      Reglas explícitas de cómo la IA debe operar: qué NO decir, qué
 *      cumplir siempre (disclaimers legales, mención de precios sólo en X
 *      condiciones, prohibición de prometer plazos, etc). El LLM lo lee
 *      como restricciones duras — no como sugerencias.
 *
 * Los cuatro subsistemas se distinguen por el campo `category` de cada
 * KnowledgeSnippet, no por endpoint. Así el motor no necesita saber en
 * qué subsistema vive cada dato.
 *
 * FLUJO EN TIEMPO DE EJECUCIÓN
 * ----------------------------
 *   Engine (Recovery / Renewal / …)
 *        │  actor, engineId, purpose
 *        ▼
 *   KnowledgePort.getKnowledgeFor(...)
 *        │
 *        ▼
 *   Este adapter ──► MCP Server (external)
 *                        │
 *                        ├── Product Bible retrieval
 *                        ├── Knowledge Base retrieval
 *                        ├── Decision Log retrieval
 *                        └── AI Operating Manual retrieval
 *                        │
 *                        ▼
 *                    Ranked snippets
 *
 *   Los snippets vuelven al motor → se pasan al LLMPort → el LLM adapter
 *   los inyecta en el prompt como contexto autorizado.
 *
 * REGLAS CONSTITUCIONALES
 * -----------------------
 * 1. Este adapter NO escribe conocimiento. Lo escribe una futura UI de
 *    Product Bible (fuera de scope V0.01), o un pipeline de ingestión de MCP.
 * 2. Este adapter NO invoca LLM. Es un lector puro.
 * 3. Este adapter NO conoce a los motores. Sólo cumple el port.
 * 4. Este adapter NO modela categorías nuevas ni relaja el schema del port.
 *    Si Product Bible / Decision Log / AI Manual necesitan estructuras
 *    específicas, se representan en el `body` (markdown / json embebido).
 *
 * ESTADO V0.01
 * ------------
 * NO IMPLEMENTADO. Sigue lanzando `MCP_KNOWLEDGE_NOT_IMPLEMENTED` en
 * runtime. Este archivo es un blueprint para Claude Code.
 *
 * ============================================================================
 * CONFIGURACIÓN ESPERADA
 * ============================================================================
 *
 *   createMcpKnowledge({
 *     serverUrl:      string,         // ej. "https://mcp.mycompany.com"
 *     apiKey:         string,         // bearer token
 *     topK?:          number,         // default 5 snippets por retrieval
 *     timeoutMs?:     number,         // default 3000
 *     minScore?:      number,         // filtrar snippets bajo este umbral (0..1)
 *     workspaceId?:   string,         // multi-tenant workspace en MCP
 *   })
 *
 * Precondición: `serverUrl` y `apiKey` presentes. Si faltan, `status()`
 * devuelve "not_configured" y los métodos operativos lanzan
 * McpNotConfiguredError.
 *
 * ============================================================================
 * CONTRATO DE PAYLOADS
 * ============================================================================
 *
 * ---- getKnowledgeFor · INPUT ----
 *
 *   {
 *     actor: Actor,                    // ver /contracts/index.js
 *     engineId: "recovery" | "renewal" | "cross_sell" | ...,
 *     purpose: "play_generation" | "value_estimation" | "objection_handling",
 *   }
 *
 * ---- getKnowledgeFor · OUTPUT (esperado del MCP server) ----
 *
 *   [
 *     {
 *       id: "product-bible:pricing-enterprise",
 *       title: "Pricing Enterprise 2026",
 *       body:  "Enterprise tier arranca en US$4,800 anual. Descuento máximo autorizado: 12% con firma > 24m.",
 *       category: "playbook",            // uno de: tone|icp|playbook|restriction|case_study|other
 *       score: 0.94
 *     },
 *     {
 *       id: "kb:tone-latam",
 *       title: "Tono LATAM formal-cercano",
 *       body:  "Español LATAM formal-cercano. Nunca 'usted' salvo primer contacto en aseguradoras.",
 *       category: "tone",
 *       score: 0.88
 *     },
 *     {
 *       id: "decision-log:recovery-9m-enterprise",
 *       title: "Playbook ganador Recovery 9m Enterprise",
 *       body:  "En últimos 6 meses, secuencia [WhatsApp+Call] cerró 68% en cuentas Enterprise con >6m inactivas.",
 *       category: "case_study",
 *       score: 0.82
 *     },
 *     {
 *       id: "ai-manual:no-promise-deadline",
 *       title: "Restricción — no prometer plazos",
 *       body:  "La IA NUNCA promete fechas de entrega. Solo dice 'coordinaremos con implementación'.",
 *       category: "restriction",
 *       score: 1.0
 *     }
 *   ]
 *
 * ---- status · OUTPUT ----
 *
 *   { status: "ready" | "empty" | "not_configured" }
 *
 *     ready           → MCP server responde y Product Bible tiene documentos indexados
 *     empty           → MCP server responde pero no hay documentos → LLM caerá a sin-contexto
 *     not_configured  → serverUrl / apiKey ausentes, o server no responde
 *
 * ============================================================================
 * ENDPOINT SUGERIDO PARA EL MCP SERVER
 * ============================================================================
 *
 *   POST {serverUrl}/tools/knowledge.retrieve
 *   Headers:
 *     Authorization: Bearer {apiKey}
 *     Content-Type: application/json
 *     X-Workspace-Id: {workspaceId}                        // si aplica
 *   Body:
 *     {
 *       query: "Recovery play for inactive Enterprise account, 9m no purchase",
 *       filters: {
 *         engineId: "recovery",
 *         purpose: "play_generation",
 *         actorVertical: "software_distributors",
 *         actorSegment: "Enterprise",
 *         actorStage: "inactive"
 *       },
 *       topK: 5
 *     }
 *   Response 200:
 *     {
 *       snippets: [ KnowledgeSnippet, ... ],
 *       trace: { queryEmbeddingModel, retrievedAt, totalCandidates }
 *     }
 *
 * `trace` es opcional y no se propaga al dominio. El adapter puede loguearlo
 * para debugging pero nunca retornarlo dentro del array de snippets.
 *
 * ============================================================================
 * TAXONOMÍA DE ERRORES
 * ============================================================================
 *
 * Todo error que este adapter lance debe pertenecer a uno de estos tipos.
 * El motor los propaga; la UI muestra un toast informativo. En NINGÚN caso
 * el motor cae por un fallo de knowledge — degrada a "sin contexto" y sigue
 * generando el play con menos precisión.
 */

export class McpKnowledgeError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "McpKnowledgeError";
    this.code = code;
    this.cause = cause;
  }
}
export class McpNotConfiguredError extends McpKnowledgeError {
  constructor(msg = "MCP no configurado: falta serverUrl o apiKey.") {
    super("MCP_NOT_CONFIGURED", msg);
  }
}
export class McpTransportError extends McpKnowledgeError {
  constructor(cause) {
    super("MCP_TRANSPORT_ERROR", "Fallo de red o timeout hacia el servidor MCP.", cause);
  }
}
export class McpAuthError extends McpKnowledgeError {
  constructor() { super("MCP_AUTH_ERROR", "El servidor MCP rechazó el apiKey."); }
}
export class McpProtocolError extends McpKnowledgeError {
  constructor(msg) { super("MCP_PROTOCOL_ERROR", msg || "Respuesta MCP fuera de contrato."); }
}
export class McpNotImplementedError extends McpKnowledgeError {
  constructor(method) {
    super("MCP_KNOWLEDGE_NOT_IMPLEMENTED",
      `MCP adapter '${method}' es un placeholder V0.01 — ver /adapters/knowledge/mcp.js.`);
  }
}

/**
 * ============================================================================
 * TODOs PARA CLAUDE CODE (en orden sugerido)
 * ============================================================================
 *
 * [ ] TODO(claude-code, step-1):
 *     Implementar `status()` con ping real a `GET {serverUrl}/health`.
 *     Timeout: config.timeoutMs ?? 3000. Retornar shape del port.
 *
 * [ ] TODO(claude-code, step-2):
 *     Implementar `buildQueryFrom({actor, engineId, purpose})` — helper
 *     interno determinista que genera la query textual. NO exportar.
 *
 * [ ] TODO(claude-code, step-3):
 *     Implementar `getKnowledgeFor({actor, engineId, purpose})`:
 *       - Validar configuración → throw McpNotConfiguredError si falta.
 *       - Construir query + filters (ver payload arriba).
 *       - POST al servidor con AbortSignal.timeout(config.timeoutMs).
 *       - Mapear 401/403 → McpAuthError.
 *       - Mapear network/timeout → McpTransportError.
 *       - Validar shape de la respuesta → McpProtocolError si viola contrato.
 *       - Filtrar por minScore si config lo especifica.
 *       - Retornar snippets ordenados por score descendente.
 *
 * [ ] TODO(claude-code, step-4):
 *     Cache opcional con TTL (60s) por hash(actor.id + engineId + purpose).
 *     Invalidación por webhook "knowledge.updated" — fuera de scope V0.01.
 *
 * [ ] TODO(claude-code, step-5):
 *     Correr `mcp.contract.test.js` con jest (`yarn test knowledge/mcp`).
 *     Los tests están escritos para pasar SOLO cuando la implementación real
 *     esté cableada. Hoy validan únicamente la superficie (shape del port).
 *
 * ============================================================================
 */

/**
 * @param {{
 *   serverUrl?: string, apiKey?: string,
 *   topK?: number, timeoutMs?: number,
 *   minScore?: number, workspaceId?: string,
 * }} [config]
 * @returns {import('../../domain/ports').KnowledgePort}
 */
export function createMcpKnowledge(config = {}) {
  const configured = Boolean(config.serverUrl && config.apiKey);

  return {
    async status() {
      // TODO(claude-code, step-1): ping real a `${config.serverUrl}/health`.
      // Hoy: sin implementación → siempre "not_configured".
      return { status: "not_configured", vendor: "mcp" };
    },

    async getKnowledgeFor(/* { actor, engineId, purpose } */) {
      if (!configured) throw new McpNotConfiguredError();
      // TODO(claude-code, step-2 y step-3): implementar retrieval.
      // Ver comentario "ENDPOINT SUGERIDO" arriba para el shape esperado.
      throw new McpNotImplementedError("getKnowledgeFor");
    },
  };
}

// Exponer error taxonomy para tests y para que otros adapters de knowledge
// puedan reusar los tipos si quieren mantener consistencia (ej. un futuro
// `adapters/knowledge/pinecone.js` podría reciclar McpTransportError).
export const ERROR_CODES = Object.freeze({
  NOT_CONFIGURED: "MCP_NOT_CONFIGURED",
  TRANSPORT: "MCP_TRANSPORT_ERROR",
  AUTH: "MCP_AUTH_ERROR",
  PROTOCOL: "MCP_PROTOCOL_ERROR",
  NOT_IMPLEMENTED: "MCP_KNOWLEDGE_NOT_IMPLEMENTED",
});
